import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { IconButton } from './button';
import { X } from './icons';

/* ────────────────────────────────────────────────────────────────────────────
   Overlays: Modal, Sheet, Tooltip, Popover.

   Modal and Sheet portal into `document.body`, trap focus, restore it on close,
   lock background scroll and close on Escape. Tooltip portals too — an absolute
   panel would be clipped by the `overflow-hidden` cards it usually lives in.
   ──────────────────────────────────────────────────────────────────────────── */

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const { body } = document;
    const previous = body.style.overflow;
    const previousPad = body.style.paddingRight;
    const gutter = window.innerWidth - body.clientWidth;
    body.style.overflow = 'hidden';
    if (gutter > 0) body.style.paddingRight = `${gutter}px`;
    return () => {
      body.style.overflow = previous;
      body.style.paddingRight = previousPad;
    };
  }, [active]);
}

function useEscape(active: boolean, onClose: () => void) {
  useEffect(() => {
    if (!active) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, onClose]);
}

/** Cycles Tab within `ref` and hands focus back to the opener on unmount. */
function useFocusTrap(active: boolean, ref: RefObject<HTMLElement>) {
  useEffect(() => {
    if (!active) return;
    const opener = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const list = () =>
      node ? Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];
    const initial = list()[0] ?? node;
    initial?.focus({ preventScroll: true });

    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Tab') return;
      const items = list();
      if (items.length === 0) {
        event.preventDefault();
        node?.focus({ preventScroll: true });
        return;
      }
      const index = items.indexOf(document.activeElement as HTMLElement);
      if (event.shiftKey && index <= 0) {
        event.preventDefault();
        items[items.length - 1]?.focus();
      } else if (!event.shiftKey && index === items.length - 1) {
        event.preventDefault();
        items[0]?.focus();
      }
    }

    node?.addEventListener('keydown', onKey);
    return () => {
      node?.removeEventListener('keydown', onKey);
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, [active, ref]);
}

/* ── Modal ────────────────────────────────────────────────────────────────── */

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
}

const MODAL_SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
} as const;

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  children,
  className,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();

  useScrollLock(open);
  useEscape(open, onClose);
  useFocusTrap(open, panelRef);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-end justify-center p-0 sm:items-center sm:p-4">
      <div
        className="glass absolute inset-0 animate-fade-in bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cn(
          'relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-t-2xl border border-line bg-surface shadow-pop outline-none animate-scale-in sm:rounded-2xl',
          MODAL_SIZES[size],
          className,
        )}
      >
        {title || description ? (
          <div className="flex items-start gap-3 border-b border-line px-5 py-4">
            <div className="flex min-w-0 flex-col gap-1">
              {title ? (
                <h2
                  id={titleId}
                  className="font-display text-base font-semibold leading-tight text-fg"
                >
                  {title}
                </h2>
              ) : null}
              {description ? (
                <p id={descId} className="text-xs leading-relaxed text-muted">
                  {description}
                </p>
              ) : null}
            </div>
            <IconButton
              label="Close dialog"
              size="sm"
              onClick={onClose}
              className="-mr-1 ml-auto shrink-0"
            >
              <X size={16} />
            </IconButton>
          </div>
        ) : (
          <IconButton
            label="Close dialog"
            size="sm"
            onClick={onClose}
            className="absolute right-3 top-3 z-10"
          >
            <X size={16} />
          </IconButton>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-line bg-elevated/40 px-5 py-3.5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/* ── Sheet ────────────────────────────────────────────────────────────────── */

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  side?: 'bottom' | 'right';
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}

export function Sheet({ open, onClose, side = 'bottom', title, children, className }: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useScrollLock(open);
  useEscape(open, onClose);
  useFocusTrap(open, panelRef);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[90] flex',
        side === 'bottom' ? 'items-end justify-center' : 'items-stretch justify-end',
      )}
    >
      <div
        className="glass absolute inset-0 animate-fade-in bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={cn(
          'relative flex flex-col overflow-hidden border-line bg-surface shadow-pop outline-none',
          side === 'bottom'
            ? 'max-h-[88vh] w-full animate-rise-in rounded-t-2xl border-t'
            : 'h-full w-full max-w-md animate-slide-in-right border-l',
          className,
        )}
      >
        {side === 'bottom' ? (
          <div aria-hidden="true" className="mx-auto mt-2.5 h-1 w-10 rounded-full bg-line-strong" />
        ) : null}
        <div className="flex items-center gap-3 px-5 py-3.5">
          {title ? (
            <h2 id={titleId} className="font-display text-base font-semibold leading-tight text-fg">
              {title}
            </h2>
          ) : null}
          <IconButton label="Close panel" size="sm" onClick={onClose} className="ml-auto shrink-0">
            <X size={16} />
          </IconButton>
        </div>
        <div className="rule shrink-0" />
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

/* ── Tooltip ──────────────────────────────────────────────────────────────── */

export type Side = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  content: ReactNode;
  side?: Side;
  /** Small pointer notch. On by default; drop it in very tight rows. */
  arrow?: boolean;
  className?: string;
  children: ReactNode;
}

const FLIP: Record<Side, Side> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
const GAP = 8;

export function Tooltip({ content, side = 'top', arrow = true, className, children }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [placed, setPlaced] = useState<Side>(side);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);
  const tipId = useId();

  const cancel = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const show = useCallback(() => {
    cancel();
    timer.current = window.setTimeout(() => setOpen(true), 120);
  }, [cancel]);

  const hide = useCallback(() => {
    cancel();
    setOpen(false);
    setPos(null);
  }, [cancel]);

  useEffect(() => cancel, [cancel]);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') hide();
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', hide, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', hide, true);
    };
  }, [open, hide]);

  useLayoutEffect(() => {
    if (!open) return;
    const trigger = wrapRef.current?.getBoundingClientRect();
    const panel = panelRef.current?.getBoundingClientRect();
    if (!trigger || !panel) return;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const fits: Record<Side, boolean> = {
      top: trigger.top - panel.height - GAP > 4,
      bottom: trigger.bottom + panel.height + GAP < vh - 4,
      left: trigger.left - panel.width - GAP > 4,
      right: trigger.right + panel.width + GAP < vw - 4,
    };

    let next = side;
    if (!fits[next]) {
      next = fits[FLIP[side]]
        ? FLIP[side]
        : ((['top', 'bottom', 'right', 'left'] as const).find((s) => fits[s]) ?? side);
    }

    let top: number;
    let left: number;
    if (next === 'top' || next === 'bottom') {
      top = next === 'top' ? trigger.top - panel.height - GAP : trigger.bottom + GAP;
      left = trigger.left + trigger.width / 2 - panel.width / 2;
    } else {
      top = trigger.top + trigger.height / 2 - panel.height / 2;
      left = next === 'left' ? trigger.left - panel.width - GAP : trigger.right + GAP;
    }

    left = Math.min(Math.max(left, 6), Math.max(6, vw - panel.width - 6));
    top = Math.min(Math.max(top, 6), Math.max(6, vh - panel.height - 6));

    setPlaced(next);
    setPos({ top, left });
  }, [open, side]);

  const arrowClass: Record<Side, string> = {
    top: 'bottom-[-3px] left-1/2 -translate-x-1/2',
    bottom: 'top-[-3px] left-1/2 -translate-x-1/2',
    left: 'right-[-3px] top-1/2 -translate-y-1/2',
    right: 'left-[-3px] top-1/2 -translate-y-1/2',
  };

  return (
    <>
      <span
        ref={wrapRef}
        className="inline-flex"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={open ? tipId : undefined}
      >
        {children}
      </span>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              id={tipId}
              role="tooltip"
              style={{ top: pos?.top ?? 0, left: pos?.left ?? 0 }}
              className={cn(
                'pointer-events-none fixed z-[120] max-w-[16rem] rounded-md border border-line bg-overlay px-2 py-1.5 text-2xs leading-snug text-fg shadow-pop',
                pos ? 'animate-fade-in opacity-100' : 'opacity-0',
                className,
              )}
            >
              {content}
              {arrow ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute h-1.5 w-1.5 rotate-45 border-b border-r border-line bg-overlay',
                    arrowClass[placed],
                  )}
                />
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/* ── Popover ──────────────────────────────────────────────────────────────── */

export interface PopoverProps {
  trigger: ReactNode;
  /**
   * Panel content. Given the render-prop form, `close` folds the panel — which is
   * what any menu whose items act on selection needs. Without it callers were
   * re-dispatching a synthetic `mousedown` on `document` to trigger the
   * click-outside handler, which is fragile and non-obvious.
   */
  children: ReactNode | ((api: { close: () => void }) => ReactNode);
  align?: 'start' | 'end';
  /** Render the trigger inline instead of wrapping it in the popover's own button.
   *  Use when the trigger already is a control, to avoid nesting interactives. */
  asChild?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

export function Popover({
  trigger,
  children,
  align = 'start',
  onOpenChange,
  className,
}: PopoverProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const change = useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  // Return focus to the trigger when the panel folds, so keyboard users don't
  // get dropped at the top of the document.
  const close = useCallback(() => {
    change(false);
    triggerRef.current?.focus();
  }, [change]);

  useEffect(() => {
    if (!open) return;
    function onDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) change(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        change(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, change]);

  return (
    <div ref={rootRef} className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => change(!open)}
        className="inline-flex min-h-6 items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/70 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
      >
        {trigger}
      </button>
      {open ? (
        <div
          role="dialog"
          className={cn(
            'absolute top-[calc(100%+0.5rem)] z-50 min-w-[12rem] animate-scale-in rounded-lg border border-line bg-overlay p-2 shadow-pop',
            align === 'start' ? 'left-0 origin-top-left' : 'right-0 origin-top-right',
            className,
          )}
        >
          {typeof children === 'function' ? children({ close }) : children}
        </div>
      ) : null}
    </div>
  );
}
